import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { KPICard } from "@/components/collections/kpi-card";
import { LogCallModal, SendEmailModal } from "@/components/collections/action-modals";
import { CreateCaseDrawer } from "@/components/collections/create-case-drawer";
import { CreatePromiseDrawer } from "@/components/collections/create-promise-drawer";
import { ColumnConfigDialog } from "@/components/collections/column-config-dialog";
import { CaseDetailsDialog } from "@/components/collections/case-details-dialog";
import { useColumnPreferences } from "@/hooks/useColumnPreferences";
import {
  Search,
  Phone,
  Mail,
  FileText,
  Clock,
  Inbox,
  AlertTriangle,
  PoundSterling,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
  FileSpreadsheet,
  Plus,
  X,
  HandshakeIcon,
  Info,
  Loader2,
} from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { cn } from "@/lib/utils";
import { type CollectionCase, type PromiseToPay } from "@/types/collections";
import { exportCollectionCasesToExcel } from "@/lib/collections-export-utils";
import { useCollectionCasesList, useCreateCollectionCase, type CreateCollectionCaseRequest } from "@/hooks/useCollectionCasesData";
import { useCreatePromise, type CreatePromiseRequest } from "@/hooks/usePromisesData";
import { toast } from "sonner";

// Helper function to get the latest action for a case
const getLatestAction = (caseItem: CollectionCase) => {
  // Generate mock activities based on case data
  const activities = [
    {
      type: "call",
      description: "Attempted phone call - no answer",
      outcome: "Left voicemail requesting callback",
      createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
    },
    {
      type: "email",
      description: "Sent payment reminder email",
      outcome: "Email delivered successfully",
      createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000), // 4 days ago
    },
    {
      type: "note",
      description: "Customer requested payment plan options",
      outcome: "Sent payment plan proposal",
      createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
    },
  ];

  // Sort by date and get the most recent
  const sortedActivities = activities.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const latestActivity = sortedActivities[0];

  return {
    type: latestActivity.type,
    description: latestActivity.description,
    outcome: latestActivity.outcome,
    date: latestActivity.createdAt,
  };
};

export default function CollectionsQueue() {
  const [searchParams] = useSearchParams();
  void useNavigate; // Available for future navigation needs
  const filterParam = searchParams.get("filter");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active"); // Default to Active cases only
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedCase, setSelectedCase] = useState<CollectionCase | null>(null);
  const [selectedCaseIds, setSelectedCaseIds] = useState<Set<string>>(new Set());
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  // Modal states
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [createPromiseOpen, setCreatePromiseOpen] = useState(false);
  const [createCaseOpen, setCreateCaseOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [caseDetailsOpen, setCaseDetailsOpen] = useState(false);
  const [selectedCaseForDetails, setSelectedCaseForDetails] = useState<CollectionCase | null>(null);
  const [isExporting, setIsExporting] = useState(false);

    // Column preferences
    const { columns, savePreferences, resetToDefaults } = useColumnPreferences();

  // Sorting state - Default sort: Action Due Date (asc), then Priority (desc)
  type SortField = "priority" | "caseId" | "customerName" | "amount" | "daysInCollection" | "nextAction" | "nextActionDate" | "status" | "strategy";
  type SortDirection = "asc" | "desc" | null;
  const [sortField, setSortField] = useState<SortField | null>("nextActionDate"); // Default primary sort
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc"); // Ascending - oldest dates first

  // Fetch collection cases using mode-aware hook (demo/live)
  const { data: casesData, isLoading: casesLoading } = useCollectionCasesList();
  const cases = casesData?.cases ?? [];

  // Create mutations(mode-aware - calls API in live mode, returns mock in demo mode)
  const createCaseMutation = useCreateCollectionCase();
  const createPromiseMutation = useCreatePromise();

  // Handle URL filter parameter
  useEffect(() => {
    if (filterParam === "action-due-today") {
      // When coming from Homepage "Cases Due" metric, show all statuses
      setStatusFilter("all");
    }
  }, [filterParam]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, priorityFilter, filterParam]);

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> back to default
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        // Reset to default sort (Action Due Date ascending)
        setSortField("nextActionDate");
        setSortDirection("asc");
      }
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-2 h-4 w-4" />;
    }
    if (sortDirection === "asc") {
      return <ArrowUp className="ml-2 h-4 w-4" />;
    }
    return <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Filter and sort cases
  const filteredAndSortedCases = useMemo(() => {
    // First, filter the cases
    let result = cases.filter((caseItem) => {
      const matchesSearch =
        caseItem.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        caseItem.caseId.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || caseItem.status === statusFilter;
      const matchesPriority = priorityFilter === "all" || caseItem.priority === priorityFilter;

      // Handle "action-due-today" filter from Homepage
      let matchesActionDueToday = true;
      if (filterParam === "action-due-today") {
        const daysDiff = differenceInDays(new Date(caseItem.nextActionDate), new Date());
        matchesActionDueToday = daysDiff === 0;
      }

      return matchesSearch && matchesStatus && matchesPriority && matchesActionDueToday;
    });

    // Then, apply sorting if a sort field is selected
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number | Date;
        let bValue: string | number | Date;

        // Custom priority order: High > Medium > Low
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };

        switch (sortField) {
          case "priority":
            aValue = priorityOrder[a.priority as keyof typeof priorityOrder];
            bValue = priorityOrder[b.priority as keyof typeof priorityOrder];
            break;
          case "caseId":
            aValue = a.caseId.toLowerCase();
            bValue = b.caseId.toLowerCase();
            break;
          case "customerName":
            aValue = a.customerName.toLowerCase();
            bValue = b.customerName.toLowerCase();
            break;
          case "amount":
            aValue = a.amount;
            bValue = b.amount;
            break;
          case "daysInCollection":
            aValue = a.daysInCollection;
            bValue = b.daysInCollection;
            break;
          case "nextAction":
            aValue = a.nextAction.toLowerCase();
            bValue = b.nextAction.toLowerCase();
            break;
          case "nextActionDate":
            aValue = new Date(a.nextActionDate).getTime();
            bValue = new Date(b.nextActionDate).getTime();
            break;
          case "status":
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          case "strategy":
            aValue = a.strategy.name.toLowerCase();
            bValue = b.strategy.name.toLowerCase();
            break;
          default:
            return 0;
        }

        // Primary sort comparison
        let comparison = 0;
        if (aValue < bValue) comparison = sortDirection === "asc" ? -1 : 1;
        else if (aValue > bValue) comparison = sortDirection === "asc" ? 1 : -1;

        // If primary sort values are equal and sorting by nextActionDate, apply secondary sort by Priority (desc)
        if (comparison === 0 && sortField === "nextActionDate") {
          const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder];
          const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder];
          // Descending order for priority (High priority first)
          if (aPriority > bPriority) return -1;
          if (aPriority < bPriority) return 1;
        }

        return comparison;
      });
    }

    return result;
  }, [cases, searchQuery, statusFilter, priorityFilter, sortField, sortDirection, filterParam]);

  // Keep filteredCases for backward compatibility with KPI calculations
  const filteredCases = filteredAndSortedCases;

  // Calculate pagination
  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCases = filteredCases.slice(startIndex, endIndex);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('ellipsis-start');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }
      
      // Always show last page
      pages.push(totalPages);
    }
    
    return pages;
  };

  // Calculate KPIs
  const kpis = useMemo(() => {
    const highPriority = filteredCases.filter((c) => c.priority === "High").length;
    const promisedCases = filteredCases.filter((c) => c.status === "Promised").length;
    const dueToday = filteredCases.filter((c) => {
      const daysDiff = differenceInDays(new Date(c.nextActionDate), new Date());
      return daysDiff === 0;
    }).length;
    const totalOutstanding = filteredCases.reduce((sum, c) => sum + c.amount, 0);
    const avgDays = Math.round(
      filteredCases.reduce((sum, c) => sum + c.daysInCollection, 0) / filteredCases.length
    );

    return {
      myCases: { total: cases.length, filtered: filteredCases.length },
      highPriority,
      promisedCases,
      dueToday,
      totalOutstanding,
      avgDays,
    };
  }, [cases, filteredCases]);

  const handleViewCaseDetails = (caseItem: CollectionCase) => {
    // Open case details in slide drawer instead of navigating to full-screen page
    setSelectedCaseForDetails(caseItem);
    setCaseDetailsOpen(true);
  };

    const handleAction = (action: string, caseItem: CollectionCase) => {
      setSelectedCase(caseItem);
      switch (action) {
        case "call":
          setLogCallOpen(true);
          break;
        case "email":
          setSendEmailOpen(true);
          break;
        case "promise":
          setCreatePromiseOpen(true);
          break;
        default:
          break;
      }
    };
    // handleAction is used in the table row actions
    void handleAction;

  // Checkbox selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredCases.map((c) => c.id));
      setSelectedCaseIds(allIds);
    } else {
      setSelectedCaseIds(new Set());
    }
  };

  const handleSelectCase = (caseId: string, checked: boolean) => {
    const newSelected = new Set(selectedCaseIds);
    if (checked) {
      newSelected.add(caseId);
    } else {
      newSelected.delete(caseId);
    }
    setSelectedCaseIds(newSelected);
  };

  // Bulk action handlers
  const handleBulkAction = (action: string) => {
    if (selectedCaseIds.size === 0) return;
    
    // For single selection, use the first selected case
    const firstSelectedId = Array.from(selectedCaseIds)[0];
    const firstCase = cases.find((c) => c.id === firstSelectedId);
    
    if (firstCase) {
      setSelectedCase(firstCase);
      switch (action) {
        case "call":
          setLogCallOpen(true);
          break;
        case "email":
          setSendEmailOpen(true);
          break;
        case "promise":
          setCreatePromiseOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const selectedCases = useMemo(() => {
    return cases.filter((c) => selectedCaseIds.has(c.id));
  }, [cases, selectedCaseIds]);

  const isAllSelected = filteredCases.length > 0 && selectedCaseIds.size === filteredCases.length;
  const isSomeSelected = selectedCaseIds.size > 0 && selectedCaseIds.size < filteredCases.length;

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority) {
      case "High":
        return "destructive";
      case "Medium":
        return "default";
      case "Low":
        return "secondary";
      default:
        return "default";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "On Hold":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
      case "Promised":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "Escalated":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "Closed":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "";
    }
  };

  const isOverdue = (date: Date) => {
    return differenceInDays(new Date(), new Date(date)) > 0;
  };

  const isDueToday = (date: Date) => {
    return differenceInDays(new Date(date), new Date()) === 0;
  };

  // Export handler - current view
  const handleExportToExcel = async () => {
    if (filteredCases.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      exportCollectionCasesToExcel(filteredCases);
    } catch (error) {
      console.error("Error exporting cases:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Export handler - all columns
  const handleExportAllColumns = async () => {
    if (filteredCases.length === 0) {
      return;
    }

    setIsExporting(true);
    try {
      exportCollectionCasesToExcel(filteredCases, `my-cases-all-columns-${new Date().toISOString().split("T")[0]}.csv`);
    } catch (error) {
      console.error("Error exporting cases:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Collections Queue</h1>
          <p className="text-muted-foreground">Manage your assigned collection cases</p>
        </div>
        <Button onClick={() => setCreateCaseOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Create New Case
        </Button>
      </div>

      {/* Loading State */}
      {casesLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading collection cases...</span>
        </div>
      )}

      {/* KPI Cards */}
      {!casesLoading && <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <KPICard
          title="My Cases"
          value={kpis.myCases.filtered}
          subtitle={`${kpis.myCases.total} total cases`}
          icon={Inbox}
          colorScheme="blue"
        />
        <KPICard
          title="Promised"
          value={kpis.promisedCases}
          subtitle="Payment commitments"
          icon={HandshakeIcon}
          colorScheme="blue"
        />
        <KPICard
          title="High Priority"
          value={kpis.highPriority}
          subtitle="Requiring immediate attention"
          icon={AlertTriangle}
          colorScheme="red"
        />
        <KPICard
          title="Due Today"
          value={kpis.dueToday}
          subtitle="Actions scheduled for today"
          icon={Calendar}
          colorScheme="amber"
        />
        <KPICard
          title="Total Outstanding"
          value={`£${kpis.totalOutstanding.toLocaleString()}`}
          subtitle={`Avg ${kpis.avgDays} days in collection`}
          icon={PoundSterling}
          colorScheme="purple"
        />
      </div>}

      {/* Filters and Search */}
      {!casesLoading && <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>My Cases</CardTitle>
              <CardDescription>View and manage your assigned collection cases</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={isExporting || filteredCases.length === 0}
                    className="h-8 w-8"
                  >
                    <FileSpreadsheet className={cn("h-4 w-4", isExporting && "animate-pulse")} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleExportToExcel}>
                    Export Current View
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportAllColumns}>
                    Export All Columns
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setColumnConfigOpen(true)}
                      aria-label="Customize Columns"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Customize Columns</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-4 md:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by customer name or case ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Promised">Promised</SelectItem>
                <SelectItem value="Escalated">Escalated</SelectItem>
                <SelectItem value="On Hold">On Hold</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-[180px]">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Cases Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      aria-label="Select all cases"
                      className={cn(isSomeSelected && !isAllSelected && "data-[state=checked]:bg-primary")}
                    />
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("priority")}
                    >
                      <span>Priority</span>
                      {getSortIcon("priority")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("caseId")}
                    >
                      <span>Case ID</span>
                      {getSortIcon("caseId")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("customerName")}
                    >
                      <span>Customer</span>
                      {getSortIcon("customerName")}
                    </Button>
                  </TableHead>
                  <TableHead className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-mr-3 h-8 data-[state=open]:bg-accent ml-auto flex"
                      onClick={() => handleSort("amount")}
                    >
                      <span>Amount</span>
                      {getSortIcon("amount")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("daysInCollection")}
                    >
                      <span>Days</span>
                      {getSortIcon("daysInCollection")}
                    </Button>
                  </TableHead>
                  <TableHead>Last Action</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("nextAction")}
                    >
                      <span>Next Action</span>
                      {getSortIcon("nextAction")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("nextActionDate")}
                    >
                      <span>Action Due Date</span>
                      {getSortIcon("nextActionDate")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("status")}
                    >
                      <span>Status</span>
                      {getSortIcon("status")}
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="-ml-3 h-8 data-[state=open]:bg-accent"
                      onClick={() => handleSort("strategy")}
                    >
                      <span>Collection Strategy</span>
                      {getSortIcon("strategy")}
                    </Button>
                  </TableHead>
                  <TableHead>Stage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedCases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-muted-foreground">
                      {filteredCases.length === 0 ? "No cases found" : "No cases on this page"}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedCases.map((caseItem) => (
                    <TableRow 
                      key={caseItem.id}
                      className={cn(
                        selectedCaseIds.has(caseItem.id) && "bg-muted/50"
                      )}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedCaseIds.has(caseItem.id)}
                          onCheckedChange={(checked) => handleSelectCase(caseItem.id, checked as boolean)}
                          aria-label={`Select case ${caseItem.caseId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityBadgeVariant(caseItem.priority)}>
                          {caseItem.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        <Button 
                          variant="link" 
                          className="h-auto p-0 font-medium hover:underline"
                          onClick={() => handleViewCaseDetails(caseItem)}
                        >
                          {caseItem.caseId}
                        </Button>
                      </TableCell>
                      <TableCell>{caseItem.customerName}</TableCell>
                      <TableCell className="text-right font-medium">
                        £{caseItem.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">{caseItem.daysInCollection}d</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center">
                                <Info className="h-4 w-4 text-muted-foreground hover:text-primary cursor-help" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <div className="space-y-2">
                                <p className="font-medium text-sm">Latest Action</p>
                                <div className="space-y-1">
                                  <p className="text-xs font-medium capitalize">{getLatestAction(caseItem).type}</p>
                                  <p className="text-xs text-muted-foreground">{getLatestAction(caseItem).description}</p>
                                  {getLatestAction(caseItem).outcome && (
                                    <p className="text-xs text-muted-foreground">
                                      <span className="font-medium">Outcome:</span> {getLatestAction(caseItem).outcome}
                                    </p>
                                  )}
                                  <p className="text-xs text-muted-foreground">
                                    {format(getLatestAction(caseItem).date, "MMM d, yyyy 'at' h:mm a")}
                                  </p>
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{caseItem.nextAction}</p>
                      </TableCell>
                      <TableCell>
                        <p
                          className={cn(
                            "text-sm",
                            isOverdue(caseItem.nextActionDate)
                              ? "text-red-600 dark:text-red-400 font-medium"
                              : isDueToday(caseItem.nextActionDate)
                              ? "text-amber-600 dark:text-amber-400 font-medium"
                              : "text-muted-foreground"
                          )}
                        >
                          {format(new Date(caseItem.nextActionDate), "dd/MM/yyyy")}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge className={getStatusBadgeColor(caseItem.status)}>
                            {caseItem.status}
                          </Badge>
                          {caseItem.status === 'Promised' && caseItem.currentPromise && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-xs text-muted-foreground">
                                    Due: {format(new Date(caseItem.currentPromise.promiseDate), "MMM d")}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className="space-y-1">
                                    <p className="font-medium">Promise Details</p>
                                    <p className="text-xs">Amount: £{caseItem.currentPromise.promisedAmount.toLocaleString()}</p>
                                    <p className="text-xs">Due: {format(new Date(caseItem.currentPromise.promiseDate), "MMM d, yyyy")}</p>
                                    <p className="text-xs">Method: {caseItem.currentPromise.confirmationMethod}</p>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-sm truncate max-w-[200px] block">
                                {caseItem.strategy.name}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{caseItem.strategy.name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 min-w-[140px]">
                                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 max-w-[100px]">
                                  <div
                                    className="bg-primary h-2 rounded-full transition-all"
                                    style={{ width: `${(caseItem.strategyPhase.currentPhase / caseItem.strategyPhase.totalPhases) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {caseItem.strategyPhase.currentPhase}/{caseItem.strategyPhase.totalPhases}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{caseItem.strategyPhase.phaseName}</p>
                              <p className="text-xs text-muted-foreground">
                                Phase {caseItem.strategyPhase.currentPhase} of {caseItem.strategyPhase.totalPhases}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {filteredCases.length > 0 && (
            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Items per page selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredCases.length)} of {filteredCases.length} cases
                </span>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        className={cn(
                          currentPage === 1 && "pointer-events-none opacity-50",
                          "cursor-pointer"
                        )}
                      />
                    </PaginationItem>
                    
                    {getPageNumbers().map((page, index) => (
                      <PaginationItem key={`${page}-${index}`}>
                        {typeof page === 'number' ? (
                          <PaginationLink
                            onClick={() => setCurrentPage(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        ) : (
                          <PaginationEllipsis />
                        )}
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        className={cn(
                          currentPage === totalPages && "pointer-events-none opacity-50",
                          "cursor-pointer"
                        )}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </div>
          )}
        </CardContent>
      </Card>}

      {/* Floating Quick Actions Menu */}
      {selectedCaseIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-5 duration-300">
          <div className="bg-primary text-primary-foreground rounded-lg shadow-2xl border border-primary/20">
            <div className="flex items-center gap-4 px-6 py-4">
              {/* Selection Info */}
              <div className="flex items-center gap-3 border-r border-primary-foreground/20 pr-4">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30">
                      {selectedCaseIds.size}
                    </Badge>
                    <span className="font-medium">
                      {selectedCaseIds.size === 1 ? "case" : "cases"} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-primary-foreground/80">
                    <span>
                      Total: <span className="font-semibold">£{selectedCases.reduce((sum, c) => sum + c.amount, 0).toLocaleString()}</span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkAction("call")}
                  className="gap-2"
                >
                  <Phone className="h-4 w-4" />
                  Log Call
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkAction("email")}
                  className="gap-2"
                >
                  <Mail className="h-4 w-4" />
                  Send Email
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleBulkAction("promise")}
                  className="gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Record Promise
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCaseIds(new Set())}
                  className="gap-2 hover:bg-primary-foreground/20"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modals */}
      {selectedCase && (
        <>
          <LogCallModal
            open={logCallOpen}
            onOpenChange={setLogCallOpen}
            customerName={selectedCase.customerName}
            customerPhone="(555) 123-4567"
          />
          <SendEmailModal
            open={sendEmailOpen}
            onOpenChange={setSendEmailOpen}
            customerName={selectedCase.customerName}
            customerEmail="customer@example.com"
          />
        </>
      )}

      {/* Create Promise Drawer */}
      <CreatePromiseDrawer
        open={createPromiseOpen}
        onOpenChange={setCreatePromiseOpen}
        prePopulatedCustomer={selectedCase ? {
          customerId: selectedCase.customerId,
          customerName: selectedCase.customerName,
          accountNumber: `ACC-${selectedCase.customerId.padStart(3, '0')}`,
        } : undefined}
        prePopulatedTransactions={selectedCase ? [
          {
            id: `${selectedCase.id}-1`,
            invoiceNumber: `INV-2024-${selectedCase.customerId.padStart(3, '0')}-001`,
            invoiceDate: new Date(2024, 0, 15),
            originalAmount: selectedCase.amount * 0.4,
            balanceRemaining: selectedCase.amount * 0.4,
            daysOverdue: selectedCase.daysInCollection,
          },
          {
            id: `${selectedCase.id}-2`,
            invoiceNumber: `INV-2024-${selectedCase.customerId.padStart(3, '0')}-002`,
            invoiceDate: new Date(2024, 1, 1),
            originalAmount: selectedCase.amount * 0.35,
            balanceRemaining: selectedCase.amount * 0.35,
            daysOverdue: Math.max(0, selectedCase.daysInCollection - 15),
          },
          {
            id: `${selectedCase.id}-3`,
            invoiceNumber: `INV-2024-${selectedCase.customerId.padStart(3, '0')}-003`,
            invoiceDate: new Date(2024, 2, 10),
            originalAmount: selectedCase.amount * 0.25,
            balanceRemaining: selectedCase.amount * 0.25,
            daysOverdue: Math.max(0, selectedCase.daysInCollection - 30),
          },
        ] : undefined}
        onSave={(promiseData) => {
          if (!selectedCase) return;
          
          // Map form data to API request format
          const request: CreatePromiseRequest = {
            customerId: parseInt(selectedCase.customerId, 10),
            promiseDate: format(promiseData.promiseDate, 'yyyy-MM-dd'),
            promisedAmount: promiseData.promisedAmount,
            confirmationMethod: promiseData.confirmationMethod.toLowerCase(),
            notes: promiseData.notes,
          };

          createPromiseMutation.mutate(request, {
            onSuccess: () => {
              toast.success('Promise to pay created successfully');
              setCreatePromiseOpen(false);
            },
            onError: (error) => {
              toast.error(`Failed to create promise: ${error.message}`);
            },
          });
        }}
      />

      {/* Column Configuration Dialog */}
      <ColumnConfigDialog
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={savePreferences}
        onReset={resetToDefaults}
      />

      {/* Create Case Drawer */}
      <CreateCaseDrawer
        open={createCaseOpen}
        onOpenChange={setCreateCaseOpen}
        onSave={(caseFormData) => {
          // Map form data to API request format
          const request: CreateCollectionCaseRequest = {
            customerId: parseInt(caseFormData.customerId, 10),
            priority: caseFormData.priority.toLowerCase(),
            // Don't send strategyId - the hardcoded demo strategies don't exist in the database
            strategyId: undefined,
            amount: caseFormData.amount,
            nextAction: caseFormData.nextAction,
            nextActionDate: caseFormData.nextActionDate,
            riskLevel: caseFormData.riskLevel.toLowerCase(),
            notes: caseFormData.notes,
          };

          createCaseMutation.mutate(request, {
            onSuccess: () => {
              toast.success('Collection case created successfully');
              setCreateCaseOpen(false);
            },
            onError: (error) => {
              toast.error(`Failed to create case: ${error.message}`);
            },
          });
        }}
      />

      {/* Case Details Drawer */}
      <CaseDetailsDialog
        open={caseDetailsOpen}
        onOpenChange={setCaseDetailsOpen}
        caseData={selectedCaseForDetails}
      />
    </div>
  );
}
